import { Button } from '@/components/ui/button';
import { SEOProps } from '@/components/ui/SEO';
import { Tag } from '@/components/ui/tag';
import { UnderlinedText } from '@/components/ui/underline-text';
import { MOTION } from '@/utils';
import { basePath, withBasePath } from '@/utils/basePath';
import { FAQ_LIST, STATUTS_LIST, SUMMARY_ITEMS } from '@/utils/data/facturationZlawyerContent';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import { type ReactNode, useRef, useState } from 'react';

gsap.registerPlugin(ScrollTrigger, SplitText);

const FACTURATION_ELECTRONIQUE_ZLAWYER_SEO: SEOProps = {
  title: 'Facturation électronique avocat : solution zLawyer conforme 2026',
  description:
    "Découvrez comment zLawyer accompagne les cabinets d'avocats dans la transition vers la facturation électronique obligatoire 2026 avec une solution conforme, sécurisée et sans surcoût.",
  keywords:
    'facturation électronique avocat, logiciel avocat, zLawyer, conformité 2026, Factur-X, plateforme agréée',
  url: `https://www.zlawyer.fr${basePath}/facturation-electronique-et-zlawyer/`,
};

// ─── Sous-composants ──────────────────────────────────────────────────────────

function SummaryLink({ active, href, label }: { active: boolean; href: string; label: string }) {
  return (
    <a
      href={href}
      className={`block py-1.5 text-sm transition-colors duration-200 ${
        active ? 'text-orange font-roboto-medium' : 'text-dark-blue/50 hover:text-orange'
      }`}
    >
      {label}
    </a>
  );
}

function Bullet({ children, icon = '•' }: { children: ReactNode; icon?: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="text-orange mt-1 shrink-0">{icon}</span>
      <div className="text-dark-blue/80">{children}</div>
    </li>
  );
}

function AccordionItem({
  isOpen,
  onToggle,
  question,
  answer,
}: {
  isOpen: boolean;
  onToggle: () => void;
  question: string;
  answer: ReactNode;
}) {
  return (
    <div className="border-dark-blue/[0.07] overflow-hidden border-b">
      <button
        className="flex w-full cursor-pointer items-center justify-between py-5 text-left transition-colors duration-200"
        type="button"
        onClick={onToggle}
      >
        <span className="text-dark-blue font-roboto-medium pr-4">{question}</span>
        <div className="shrink-0">
          <svg
            fill="none"
            height="18"
            viewBox="0 0 24 24"
            width="18"
            className={`transform transition-all duration-400 ease-in-out ${
              isOpen ? 'text-orange rotate-180' : 'text-dark-blue/30'
            }`}
          >
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
            />
          </svg>
        </div>
      </button>
      <div
        className={`grid transition-all duration-400 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="text-dark-blue/70 pb-6 leading-relaxed">{answer}</div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function FacturationElectroniqueEtZlawyer() {
  const [activeSection, setActiveSection] = useState('partenariat');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [openStatuts, setOpenStatuts] = useState<number | null>(null);

  const refs = {
    section: useRef<HTMLDivElement>(null),
    tag: useRef<HTMLDivElement>(null),
    title: useRef<HTMLHeadingElement>(null),
    subtitle: useRef<HTMLParagraphElement>(null),
  };

  // ─── Animation hero ───────────────────────────────────────────────────────
  useGSAP(
    () => {
      if (!refs.section.current || !refs.tag.current || !refs.title.current) return;

      const splitTitle = new SplitText(refs.title.current, { mask: 'lines', type: 'lines' });
      const splitSub = refs.subtitle.current
        ? new SplitText(refs.subtitle.current, { mask: 'lines', type: 'lines' })
        : null;

      gsap.set(refs.tag.current, { autoAlpha: 0, y: 14 });

      gsap
        .timeline({ defaults: { duration: MOTION.durationMd, ease: MOTION.ease } })
        .to(refs.tag.current, { autoAlpha: 1, y: 0, duration: MOTION.durationSm })
        .from(
          splitTitle.lines,
          { stagger: MOTION.staggerLines, yPercent: MOTION.yPercent },
          '-=0.1',
        )
        .from(
          splitSub?.lines ?? [],
          { stagger: MOTION.staggerLines, yPercent: MOTION.yPercent },
          '<+0.15',
        );
    },
    { scope: refs.section },
  );

  // ─── ScrollTrigger pour le sommaire actif ─────────────────────────────────
  useGSAP(() => {
    const sections = Array.from(document.querySelectorAll<HTMLElement>('section[id]'));
    if (!sections.length) return;

    const triggers = sections.map((section) =>
      ScrollTrigger.create({
        trigger: section,
        start: 'top 125px',
        end: 'bottom center',
        onEnter: () => setActiveSection(section.id),
        onEnterBack: () => setActiveSection(section.id),
      }),
    );

    ScrollTrigger.refresh();

    return () => {
      triggers.forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div ref={refs.section}>
      <div className="max-w-custom mx-auto px-[clamp(20px,5vw,100px)] py-20">
        {/* ─── Hero ────────────────────────────────────────────────────────── */}
        <div className="grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div className="flex flex-col gap-5">
            <Tag ref={refs.tag}>Facturation électronique et zLawyer</Tag>

            <h1 ref={refs.title}>
              zLawyer <UnderlinedText>anticipe la réforme</UnderlinedText> de la facturation
              électronique et <UnderlinedText>structure la transition</UnderlinedText> des cabinets
              d&apos;avocats
            </h1>

            <p ref={refs.subtitle} className="text-dark-blue/70">
              La réforme de la <span className="font-bold">facturation électronique</span>,
              applicable à compter de 2026, marque une évolution profonde des obligations
              applicables aux cabinets d&apos;avocats. zLawyer anticipe cette évolution en intégrant
              la facturation électronique <span className="font-bold">sans surcoût</span> au cœur de
              sa solution.
            </p>

            <Button className="mt-2 w-fit" href="/contact/" variant="transparent">
              Je souhaite être contacté
            </Button>
          </div>

          <Image
            alt="illustration du logiciel"
            className="w-full rounded-(--radius-current)"
            height={600}
            src={withBasePath('/images/features/features10.webp')}
            width={900}
          />
        </div>

        {/* ─── Contenu + Sommaire ───────────────────────────────────────────── */}
        <div className="border-dark-blue/[0.07] mt-24 flex w-full gap-12 border-t pt-12">
          {/* Aside */}
          <aside className="hidden lg:block">
            <div className="border-dark-blue/[0.07] bg-orange/2 sticky top-32 left-0 h-fit w-60 rounded-2xl border px-6 py-5">
              <p className="text-dark-blue font-roboto-medium mb-4 text-xs uppercase">Sommaire</p>
              <ul className="flex flex-col">
                {SUMMARY_ITEMS.map((item) => (
                  <li key={item.href}>
                    <SummaryLink
                      active={activeSection === item.href.slice(1)}
                      href={item.href}
                      label={item.label}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Sections */}
          <div className="w-full min-w-0">
            <section className="scroll-mt-32 pb-16" id="partenariat">
              <h2 className="mb-5">
                Facturation électronique pour les avocats : <UnderlinedText>zLawyer</UnderlinedText>{' '}
                s'impose comme partenaire stratégique des cabinets
              </h2>
              <p className="text-dark-blue/70 leading-relaxed">
                La réforme de la{' '}
                <span className="font-bold">
                  facturation électronique obligatoire à compter de 2026
                </span>{' '}
                transforme durablement l'environnement réglementaire des cabinets d'avocats.
              </p>
              <p className="text-dark-blue/70 mt-4">
                L'émission et la réception de factures électroniques, la transmission des données
                selon des formats structurés tels que <span className="font-bold">Factur-X</span>,
                et l'adaptation des processus internes deviennent des enjeux centraux pour la
                gestion des cabinets.
              </p>
              <p className="text-dark-blue/70 mt-4">
                Face à cette évolution, zLawyer ne se contente pas d'adapter sa solution :
                <br />
                <span className="font-bold">
                  zLawyer structure et anticipe la transition des cabinets d'avocats vers la
                  facturation électronique
                </span>
                .
              </p>
            </section>

            <section className="scroll-mt-32 pb-16" id="continuite">
              <h2 className="mb-5">zLawyer, logiciel avocat prêt pour la réforme 2026</h2>
              <p className="text-dark-blue/70">
                Conçu exclusivement pour les professionnels du droit,{' '}
                <span className="font-bold">
                  zLawyer est un logiciel de gestion pour cabinet d'avocats
                </span>{' '}
                intégrant :
              </p>
              <ul className="mt-4 mb-5 flex flex-col gap-2">
                <Bullet>La gestion des dossiers,</Bullet>
                <Bullet>Le suivi des temps et des honoraires,</Bullet>
                <Bullet>La facturation,</Bullet>
                <Bullet>
                  Et désormais la{' '}
                  <span className="font-bold">
                    facturation électronique conforme aux exigences 2026
                  </span>
                  .
                </Bullet>
              </ul>
              <p className="text-dark-blue/70">
                La facturation électronique est intégrée nativement{' '}
                <span className="font-bold">et sans surcoût</span> dans l'environnement zLawyer afin
                de garantir :
              </p>
              <ul className="mt-4 flex flex-col gap-2">
                <Bullet>La continuité organisationnelle du cabinet,</Bullet>
                <Bullet>L'absence de double outil,</Bullet>
                <Bullet>La centralisation des flux juridiques et financiers.</Bullet>
              </ul>
            </section>

            <section className="scroll-mt-32 pb-16" id="conformite">
              <h2 className="mb-5">
                Un partenariat stratégique avec Pennylane au service des cabinets d'avocats
              </h2>
              <p className="text-dark-blue/70">
                Pour accompagner cette transformation réglementaire, zLawyer a noué un partenariat
                stratégique avec{' '}
                <a
                  className="text-orange underline"
                  href="https://www.pennylane.com/fr/logiciel-facturation-electronique"
                  rel="noreferrer"
                  target="_blank"
                >
                  Pennylane
                </a>
                , plateforme agréée de référence, gage de conformité et de fiabilité.
              </p>
              <p className="text-dark-blue/70 my-2">
                Ce partenariat permet d'intégrer directement dans zLawyer :
              </p>
              <ul className="mt-4 mb-6 flex flex-col gap-2">
                <Bullet>
                  L'émission et la réception de factures électroniques au format{' '}
                  <span className="font-bold">Factur-X</span>,
                </Bullet>
                <Bullet>
                  La transmission des données selon les modalités prévues par la réforme,
                </Bullet>
                <Bullet>Un suivi structuré du cycle de vie des factures,</Bullet>
                <Bullet>
                  Une infrastructure conçue pour répondre aux standards de sécurité et d'évolutivité
                  attendus.
                </Bullet>
              </ul>
              <p className="text-dark-blue/70">
                La solution est conçue pour évoluer en fonction des adaptations du cadre
                réglementaire applicable aux entreprises.
              </p>
            </section>

            <section className="scroll-mt-32 pb-16" id="approche-metier">
              <h2 className="mb-5">
                Facturation électronique cabinet d'avocats : une approche métier, pas générique
              </h2>
              <p className="text-dark-blue/70">
                <span className="font-bold">
                  La réforme 2026 ne concerne pas seulement la technologie.
                </span>{' '}
                Elle impacte l'organisation interne, la gestion financière et la relation client.
              </p>
              <p className="text-dark-blue/70 mt-4">
                <span className="font-bold">zLawyer adopte une approche structurée</span> :
              </p>
              <ul className="mt-4 mb-6 flex flex-col gap-2">
                <Bullet icon="✓">Intégration native dans notre logiciel avocat,</Bullet>
                <Bullet icon="✓">Gestion centralisée des dossiers, temps et factures,</Bullet>
                <Bullet icon="✓">Sécurisation des échanges de données*,</Bullet>
                <Bullet icon="✓">Accompagnement dans la mise en œuvre opérationnelle.</Bullet>
              </ul>
              <p className="text-dark-blue/70">
                La fonctionnalité de facturation électronique est incluse dans l'abonnement et donc
                <span className="font-bold"> sans surcoût.</span>
              </p>
            </section>

            <section className="scroll-mt-32 pb-16" id="performance-cabinet">
              <h2 className="mb-5">Anticiper la conformité, renforcer la performance du cabinet</h2>
              <p className="text-dark-blue/70 my-2">
                <span className="font-bold">
                  Choisir zLawyer pour la facturation électronique, c'est :
                </span>
              </p>
              <ul className="mt-4 mb-6 flex flex-col gap-2">
                <Bullet>Anticiper les obligations réglementaires,</Bullet>
                <Bullet>Sécuriser les processus de facturation*,</Bullet>
                <Bullet>Maintenir la fluidité organisationnelle du cabinet,</Bullet>
                <Bullet>S'appuyer sur un partenaire technologique engagé dans la durée.</Bullet>
              </ul>
              <p className="text-dark-blue/70">
                Au-delà de la conformité, zLawyer transforme la réforme en opportunité d'optimiser
                et de structurer durablement la gestion de votre cabinet.
              </p>
              <p className="text-dark-blue/40 mt-4 text-sm italic">
                *Vos données sont protégées par une infrastructure certifiée ISO 27001, déployée sur
                serveur SecNumCloud.
              </p>
            </section>

            <section className="scroll-mt-32 pb-16" id="preparation">
              <h2 className="mb-5">
                Préparez dès maintenant votre cabinet à la facturation électronique obligatoire
              </h2>
              <p className="text-dark-blue/70">
                Chaque cabinet présente des spécificités, nos équipes vous accompagnent pour :
              </p>
              <ul className="mt-4 flex flex-col gap-2">
                <Bullet icon="✓">Analyser votre organisation actuelle,</Bullet>
                <Bullet icon="✓">
                  Anticiper l'impact de la réforme de la facturation électronique,
                </Bullet>
                <Bullet icon="✓">Structurer la mise en œuvre au sein de votre cabinet.</Bullet>
              </ul>
            </section>

            {/* ─── CTA ─────────────────────────────────────────────────────── */}
            <div className="border-dark-blue/[0.07] my-14 flex flex-col items-center gap-4 border-y py-12 text-center">
              <h2>
                <UnderlinedText>Besoin</UnderlinedText> d&apos;en savoir plus ?
              </h2>
              <p className="text-dark-blue/60">
                Contactez-nous dès maintenant pour une démonstration personnalisée de zLawyer
              </p>
              <Button className="mt-2" href="/contact/" variant="transparent">
                Je souhaite être contacté
              </Button>
            </div>

            <section className="mb-16 scroll-mt-32" id="faq">
              <h2 className="mb-2">
                <UnderlinedText>FAQ</UnderlinedText> – Questions fréquentes
              </h2>
              <p className="text-dark-blue/50 mb-8">
                Voici le Forum des Questions (FAQ) sur la facturation électronique
              </p>

              <div className="border-dark-blue/[0.07] border-t">
                {FAQ_LIST.map((item, idx) => (
                  <AccordionItem
                    key={item.q}
                    answer={<div dangerouslySetInnerHTML={{ __html: item.a }} />}
                    isOpen={openFaq === idx}
                    question={item.q}
                    onToggle={() => setOpenFaq(openFaq === idx ? null : idx)}
                  />
                ))}
              </div>
            </section>

            <section className="mb-16 scroll-mt-32" id="statuts-factures">
              <h2 className="mb-2">
                Les principaux statuts d&apos;une facture qui seront mis en place
              </h2>
              <p className="text-dark-blue/60 mb-8">
                Une facture peut passer par plusieurs statuts normalisés tout au long de son cycle :
              </p>

              <div className="border-dark-blue/[0.07] border-t">
                {STATUTS_LIST.map((item, idx) => (
                  <AccordionItem
                    key={item.q}
                    answer={<div dangerouslySetInnerHTML={{ __html: item.a }} />}
                    isOpen={openStatuts === idx}
                    question={item.q}
                    onToggle={() => setOpenStatuts(openStatuts === idx ? null : idx)}
                  />
                ))}
              </div>

              <div className="border-orange/25 bg-orange/5 mt-8 rounded-xl border px-6 py-5">
                <p className="text-dark-blue/70 font-roboto-medium text-sm">
                  Chaque changement de statut est remonté en temps réel, offrant une visibilité
                  complète sur l&apos;état d&apos;avancement des factures.
                </p>
              </div>
            </section>

            {/* ─── CTA final ───────────────────────────────────────────────── */}
            <div className="border-dark-blue/[0.07] flex flex-col items-center gap-4 border-t pt-12 text-center">
              <h2>
                <UnderlinedText>Besoin</UnderlinedText> d&apos;en savoir plus ?
              </h2>
              <p className="text-dark-blue/60">
                Contactez-nous dès maintenant pour une démonstration personnalisée de zLawyer
              </p>
              <Button className="mt-2" href="/contact/" variant="transparent">
                Je souhaite être contacté
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

FacturationElectroniqueEtZlawyer.seo = FACTURATION_ELECTRONIQUE_ZLAWYER_SEO;
